// Per-channel message pricing — the one place the Console states what a message
// costs on a metered delivery channel.
//
// Why this file exists: a monthly plan buys the SamSurveyor platform. It does
// not buy the messages. Some channels hand SamSurveyor a real per-message bill
// from an outside company. WhatsApp is the first one. Facebook Messenger, SMS
// and RCS will each have their own rates. Those rates are not the same, so one
// blanket "messaging is included" statement would be false.
//
// The author never sees the breakout of who gets paid what. The author sees the
// single customer price. The provider/platform split is billing-system detail
// and belongs in the usage ledger, not in the interface.
//
// Channel ids match `channelInventory.js` so the two files can be joined.
//
// Governing design: docs/system documentation library/channel-gate/whatsapp.md
// (merged 2026-08-25; section numbers from the old design doc no longer apply — see the Pricing section).

// The schedule version. Every usage record must store the version that priced
// it, so a later rate change never silently rewrites an old charge.
export const PRICING_SCHEDULE_VERSION = '2026-08-12';

// How the effective date is shown to a person.
export const PRICING_EFFECTIVE_DATE = 'August 12, 2026';

// The short line that appears on every plan card and on the public price list.
export const PER_CHANNEL_COST_NOTE = 'Additional per-channel costs may apply';

// The paragraph that introduces the detailed table.
export const PER_CHANNEL_COST_INTRO =
    'Your monthly plan covers the SamSurveyor platform. It does not cover the ' +
    'cost of messages on channels that bill per message. Those charges are ' +
    'metered separately and are based on messages actually delivered, not on ' +
    'messages attempted.';

// status values:
//   'included'  - no per-message charge on this channel
//   'metered'   - each message is charged, see rates
//   'pending'   - the channel is not open yet and has no published rates
export const CHANNEL_MESSAGE_PRICING = [
    {
        id: 'web',
        name: 'Web',
        status: 'included',
        summary:
            'No per-message charge. The browser survey and the email that ' +
            'carries its invitation are covered by your plan.',
        rates: [],
    },
    {
        id: 'google_chat',
        name: 'Google Chat',
        status: 'included',
        summary:
            'No per-message charge. Google does not bill SamSurveyor for Chat ' +
            'messages.',
        rates: [],
    },
    {
        id: 'whatsapp',
        name: 'WhatsApp',
        status: 'metered',
        summary:
            'Every message SamSurveyor sends in WhatsApp is charged. Replies ' +
            'from the recipient are not charged.',
        rates: [
            {
                event: 'Direct WhatsApp invitation, once delivered',
                price: '$0.034',
                note: 'United States numbers. Other countries cost more or less.',
            },
            {
                event: 'Each message SamSurveyor sends during the survey',
                price: '$0.009',
                note: '',
            },
            {
                event: 'Email launch invitation',
                price: 'No charge',
                note: 'The invitation is an email, so there is no WhatsApp opening fee.',
            },
            {
                event: "The recipient's reply",
                price: 'No charge',
                note: '',
            },
        ],
        example:
            'A ten-question WhatsApp survey runs about $0.13 per completed ' +
            'response when the invitation is sent directly in WhatsApp, or ' +
            'about $0.10 when it is sent as an email launch.',
    },
    {
        id: 'facebook_messenger',
        name: 'Facebook Messenger',
        status: 'pending',
        summary:
            'Messenger charges per message and will have its own rates. They ' +
            'are published here before the channel opens.',
        rates: [],
    },
    {
        id: 'sms',
        name: 'SMS',
        status: 'pending',
        summary:
            'Carriers charge per message and rates differ by country. Rates ' +
            'are published here before the channel opens.',
        rates: [],
    },
    {
        id: 'rcs',
        name: 'RCS',
        status: 'pending',
        summary:
            'Carriers charge per message and rates differ by country. Rates ' +
            'are published here before the channel opens.',
        rates: [],
    },
];

// Channels that actually bill per message today.
export const meteredChannels = () =>
    CHANNEL_MESSAGE_PRICING.filter(c => c.status === 'metered');

// Look one up by the id used in channelInventory.js.
export const channelPricing = (id) =>
    CHANNEL_MESSAGE_PRICING.find(c => c.id === id) || null;
